import React, { useState } from "react";
import { Plus, MonitorPlay } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useData } from "@/contexts/DataContext";
import MainLayout from "@/components/layouts/MainLayout";
import ScreenCard from "@/components/screens/ScreenCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Search } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { Screen } from "@/models/screen";

const formSchema = z.object({
  name: z.string().min(1, "El nombre es requerido"),
  location: z.string().min(1, "La ubicación es requerida"),
  totalSpaces: z.number().min(1, "Debe tener al menos 1 espacio"),
  occupiedSpaces: z.number().min(0, "No puede ser negativo")
}).refine(data => data.occupiedSpaces <= data.totalSpaces, {
  message: "Espacios ocupados no puede ser mayor que espacios totales",
  path: ["occupiedSpaces"]
});

const Screens = () => {
  const { isAdmin } = useAuth();
  const { screens, addScreen, updateScreen, refreshData } = useData();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedScreen, setSelectedScreen] = useState<Screen | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      location: "",
      totalSpaces: 10,
      occupiedSpaces: 0
    }
  });

  const openAddDialog = () => {
    form.reset({
      name: "",
      location: "",
      totalSpaces: 10,
      occupiedSpaces: 0
    });
    setIsAddDialogOpen(true);
  };

  const openEditDialog = (screen: Screen) => {
    form.reset({
      name: screen.name,
      location: screen.location,
      totalSpaces: screen.totalSpaces,
      occupiedSpaces: screen.occupiedSpaces
    });
    setSelectedScreen(screen);
    setIsEditDialogOpen(true);
  };

  const handleAddScreen = (data: any) => {
    addScreen({
      name: data.name,
      location: data.location,
      totalSpaces: data.totalSpaces,
      occupiedSpaces: data.occupiedSpaces
    });
    setIsAddDialogOpen(false);
    toast.success("Pantalla creada exitosamente");
  };

  const handleEditScreen = (data: any) => {
    if (selectedScreen) {
      updateScreen({
        ...selectedScreen,
        name: data.name,
        location: data.location,
        totalSpaces: data.totalSpaces,
        occupiedSpaces: data.occupiedSpaces
      });
      setIsEditDialogOpen(false);
      setSelectedScreen(null);
      toast.success("Pantalla actualizada exitosamente");
    }
  };

  // Filtramos las pantallas según el término de búsqueda
  const filteredScreens = searchTerm 
    ? screens.filter(screen => 
        screen.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
        screen.location.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : screens;

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Pantallas</h1>
            <p className="text-muted-foreground">
              Administra las pantallas disponibles
            </p>
          </div>
          <div className="flex gap-4 items-center">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input 
                placeholder="Buscar pantalla..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 w-64"
              />
            </div>
            {isAdmin && (
              <Button onClick={openAddDialog}>
                <Plus className="mr-1 h-4 w-4" /> Agregar pantalla
              </Button>
            )}
          </div>
        </div>

        {/* Screens Grid */}
        {filteredScreens.length === 0 ? (
          <div className="col-span-3 flex flex-col items-center justify-center p-8 text-center">
            <MonitorPlay className="h-16 w-16 text-muted-foreground/30 mb-4" />
            <h3 className="font-medium text-lg">No hay pantallas</h3>
            <p className="text-muted-foreground">
              {isAdmin 
                ? searchTerm 
                  ? "No se encontraron pantallas con ese criterio" 
                  : "Agrega una pantalla para comenzar" 
                : "No hay pantallas disponibles"}
            </p>
            {isAdmin && !searchTerm && (
              <Button onClick={openAddDialog} className="mt-4">
                <Plus className="mr-1 h-4 w-4" /> Agregar pantalla
              </Button>
            )}
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredScreens.map((screen) => (
              <ScreenCard 
                key={screen.id} 
                screen={screen} 
                onEdit={openEditDialog}
              />
            ))}
          </div>
        )}
      </div>

      {/* Add Screen Dialog */}
      {isAdmin && (
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Agregar pantalla</DialogTitle>
              <DialogDescription>
                Completa la información para agregar una nueva pantalla
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={form.handleSubmit(handleAddScreen)}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nombre de la pantalla</Label>
                  <Input
                    id="name"
                    placeholder="Ej: Pantalla Centro Comercial"
                    {...form.register("name")}
                  />
                  {form.formState.errors.name && (
                    <p className="text-sm text-red-500">{form.formState.errors.name.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="location">Ubicación</Label>
                  <Input
                    id="location"
                    placeholder="Ej: Av. Principal 123"
                    {...form.register("location")}
                  />
                  {form.formState.errors.location && (
                    <p className="text-sm text-red-500">{form.formState.errors.location.message}</p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="totalSpaces">Espacios totales</Label>
                    <Controller
                      name="totalSpaces"
                      control={form.control}
                      render={({ field }) => (
                        <Input
                          id="totalSpaces"
                          type="number"
                          min="1"
                          onChange={(e) => field.onChange(parseInt(e.target.value))}
                          value={field.value}
                        />
                      )}
                    />
                    {form.formState.errors.totalSpaces && (
                      <p className="text-sm text-red-500">{form.formState.errors.totalSpaces.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="occupiedSpaces">Espacios ocupados</Label>
                    <Controller
                      name="occupiedSpaces"
                      control={form.control}
                      render={({ field }) => (
                        <Input
                          id="occupiedSpaces"
                          type="number"
                          min="0"
                          onChange={(e) => field.onChange(parseInt(e.target.value))}
                          value={field.value}
                        />
                      )}
                    />
                    {form.formState.errors.occupiedSpaces && (
                      <p className="text-sm text-red-500">{form.formState.errors.occupiedSpaces.message}</p>
                    )}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" type="button" onClick={() => setIsAddDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit">Guardar</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Screen Dialog */}
      {isAdmin && (
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Editar pantalla</DialogTitle>
              <DialogDescription>
                Modifica la información de la pantalla
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={form.handleSubmit(handleEditScreen)}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nombre de la pantalla</Label>
                  <Input
                    id="name"
                    placeholder="Ej: Pantalla Centro Comercial"
                    {...form.register("name")}
                  />
                  {form.formState.errors.name && (
                    <p className="text-sm text-red-500">{form.formState.errors.name.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="location">Ubicación</Label>
                  <Input
                    id="location"
                    placeholder="Ej: Av. Principal 123"
                    {...form.register("location")}
                  />
                  {form.formState.errors.location && (
                    <p className="text-sm text-red-500">{form.formState.errors.location.message}</p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="totalSpaces">Espacios totales</Label>
                    <Controller
                      name="totalSpaces"
                      control={form.control}
                      render={({ field }) => (
                        <Input
                          id="totalSpaces"
                          type="number"
                          min="1"
                          onChange={(e) => field.onChange(parseInt(e.target.value))}
                          value={field.value}
                        />
                      )}
                    />
                    {form.formState.errors.totalSpaces && (
                      <p className="text-sm text-red-500">{form.formState.errors.totalSpaces.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="occupiedSpaces">Espacios ocupados</Label>
                    <Controller
                      name="occupiedSpaces"
                      control={form.control}
                      render={({ field }) => (
                        <Input
                          id="occupiedSpaces"
                          type="number"
                          min="0"
                          onChange={(e) => field.onChange(parseInt(e.target.value))}
                          value={field.value}
                        />
                      )}
                    />
                    {form.formState.errors.occupiedSpaces && (
                      <p className="text-sm text-red-500">{form.formState.errors.occupiedSpaces.message}</p>
                    )}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" type="button" onClick={() => setIsEditDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit">Guardar cambios</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </MainLayout>
  );
};

export default Screens;
